import React, { useState, useContext, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
  Zap,
  Cpu,
  Sun,
  Moon,
  Menu,
  X,
  Download,
  CheckCircle,
  Sparkles,
  Monitor,
  Cloud,
  Lock,
  Gauge,
  HardDrive,
  GitBranch,
  Layers,
  Linkedin,
  Twitter,
  ChevronRight,
  Shield,
  Rocket,
  Brain,
} from 'lucide-react';
import { ThemeContext } from './App.tsx';



const customStyles = `
  :root {
    --bg-color: #ffffff;
    --text-color: #111827;
    --text-color-secondary: #4B5563;
    --card-bg: #f9fafb;
    --card-border: #e5e7eb;
    --header-bg: rgba(255, 255, 255, 0.7);
    --gradient-text-from: #0EA5E9; /* Sky Blue */
    --gradient-text-to: #14B8A6;   /* Teal */
    --brand-color-lime: #10B981;
    --accent-blue: #3B82F6;      /* Professional Blue */
    --accent-cyan: #06B6D4;
    --accent-orange: #F59E0B;
  }

  [data-theme='dark'] {
    --bg-color: #030014;
    --text-color: #F9FAFB;
    --text-color-secondary: #9CA3AF;
    --card-bg: rgba(17, 24, 39, 0.5);
    --card-border: rgba(55, 65, 81, 0.5);
    --header-bg: rgba(0, 0, 0, 0.5);
    --gradient-text-from: #38BDF8; /* Brighter Sky Blue */
    --gradient-text-to: #2DD4BF;   /* Brighter Teal */
    --brand-color-lime: #34D399;
    --accent-blue: #60A5FA;      /* Brighter Professional Blue */
    --accent-cyan: #22D3EE;
    --accent-orange: #FBBF24;
  }

  body {
    background-color: var(--bg-color);
    color: var(--text-color);
    transition: background-color 0.3s ease, color 0.3s ease;
  }

  .gradient-text-flow {
    background: linear-gradient(90deg,
      var(--gradient-text-from),
      var(--gradient-text-to),
      var(--gradient-text-from)
    );
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: gradient-flow 8s linear infinite;
  }

  @keyframes gradient-flow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  .glass-card {
    background: var(--card-bg);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid var(--card-border);
    transition: all 0.3s ease-in-out;
  }

  .hover-lift {
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }

  .hover-lift:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }

  [data-theme='dark'] .hover-lift:hover {
    box-shadow: 0 10px 40px rgba(59, 130, 246, 0.2);
  }

  .glow-button {
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #0EA5E9, #3B82F6);
    transition: all 0.3s ease;
    color: #fff;
    text-decoration: none;
  }

  [data-theme='dark'] .glow-button {
    background: linear-gradient(135deg, #38BDF8, #60A5FA);
    color: #030014;
  }

  .glow-button:hover {
    box-shadow: 0 0 30px rgba(59, 130, 246, 0.6), 0 0 20px rgba(14, 165, 233, 0.4);
    transform: scale(1.05);
  }

  .geometric-layer {
    position: absolute;
    opacity: 0.1;
    transition: all 0.6s ease;
  }

  .geometric-layer:hover {
    opacity: 0.15;
    transform: scale(1.05) rotate(3deg);
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }

  .floating {
    animation: float 6s ease-in-out infinite;
  }

  @keyframes pulse-glow {
    0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.2); }
    50% { box-shadow: 0 0 40px rgba(59, 130, 246, 0.4); }
  }

  .pulse-glow {
    animation: pulse-glow 4s ease-in-out infinite;
  }
`;

const TransformerEngine = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.8]);

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/Team", label: "Team" },
    { href: "/computer-brain-interface", label: "SoulDubu" },
    { href: "/#download", label: "Download" },
  ];

  const features = [
    {
      icon: HardDrive,
      title: "Featherlight Footprint",
      description: "Only 1-1.5 GB of disk space after full installation with GPU acceleration",
      color: "var(--accent-cyan)",
    },
    {
      icon: Gauge,
      title: "Blazing-Fast Speed",
      description: "Built from the ground up in C++ for low-latency, real-time AI interaction",
      color: "var(--accent-orange)",
    },
    {
      icon: Monitor,
      title: "Universal Compatibility",
      description: "Windows 10/11/Server, macOS with MLX for Apple Silicon, and all major Linux distributions",
      color: "var(--accent-blue)",
    },
    {
      icon: Zap,
      title: "Flexible Acceleration",
      description: "CPU-only for accessibility, NVIDIA CUDA for power. AMD & Intel GPU support coming soon",
      color: "var(--brand-color-lime)",
    },
  ];

  const stats = [
    { value: "1-1.5GB", label: "Total Size", icon: HardDrive },
    { value: "<50ms", label: "Inference Time", icon: Gauge },
    { value: "100%", label: "Privacy", icon: Lock },
    { value: "0", label: "Cloud Dependency", icon: Shield },
  ];

  return (
    <>
      <style>{customStyles}</style>
      <div className="min-h-screen text-[var(--text-color)] overflow-x-hidden" style={{ backgroundColor: 'var(--bg-color)' }}>
        {/* Geometric Background Elements */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="geometric-layer w-96 h-96 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full blur-3xl top-10 -left-20" />
          <div className="geometric-layer w-80 h-80 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full blur-3xl bottom-20 -right-20" />
        </div>

        {/* Header */}
        <motion.header
          className="bg-[var(--header-bg)] backdrop-filter backdrop-blur-md fixed w-full z-50 shadow-md border-b border-[var(--card-border)]"
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <nav className="container mx-auto px-4 sm:px-6 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-8">
                <motion.div className="flex items-center space-x-2" whileHover={{ scale: 1.05 }}>
                  <Brain size={32} className="text-[var(--gradient-text-from)]" />
                  <span className="text-xl font-bold text-[var(--text-color)]">QueenDahyun AGI</span>
                </motion.div>
                <div className="hidden md:flex space-x-6">
                  {navLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="text-[var(--text-color-secondary)] hover:text-[var(--text-color)] transition duration-300"
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-6">
                <motion.button
                  onClick={toggleTheme}
                  className="p-2 rounded-full hover:bg-[var(--card-border)] text-[var(--text-color-secondary)] transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <AnimatePresence mode="wait">
                    {theme === 'light' ? (
                      <motion.div key="moon" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
                        <Moon size={20} />
                      </motion.div>
                    ) : (
                      <motion.div key="sun" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
                        <Sun size={20} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
                <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-[var(--text-color-secondary)]">
                  {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
                </button>
              </div>
            </div>
          </nav>
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                className="md:hidden bg-[var(--header-bg)] backdrop-filter backdrop-blur-lg"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="block py-3 px-6 text-[var(--text-color)] hover:bg-[var(--card-border)]"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.header>

        {/* Hero Section */}
        <section className="pt-32 pb-24 md:pt-48 md:pb-32 min-h-screen flex items-center relative overflow-hidden">
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left: Text Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                style={{ opacity, scale }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="inline-flex items-center space-x-2 glass-card px-4 py-2 rounded-full mb-6"
                >
                  <Sparkles className="text-[var(--accent-cyan)]" size={20} />
                  <span className="text-sm font-semibold text-[var(--text-color)]">Introducing</span>
                </motion.div>

                <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
                  <span className="gradient-text-flow">The Transformer</span>
                  <br />
                  <span className="text-[var(--text-color)]">Engine</span>
                </h1>

                <p className="text-xl md:text-2xl text-[var(--text-color-secondary)] mb-8 leading-relaxed">
                  Unleash the Power of AI, <span className="font-bold text-[var(--accent-blue)]">Anywhere</span>
                </p>

                <p className="text-lg text-[var(--text-color-secondary)] mb-10 leading-relaxed">
                  Our proprietary, hyper-efficient inference engine, designed to run the world's most advanced AI models with unprecedented speed and a minimal footprint.
                </p>

                <motion.a
                  href="/#download"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="glow-button py-4 px-8 rounded-full text-lg font-semibold shadow-xl inline-flex items-center"
                >
                  <Download className="mr-2" size={24} />
                  Download Now
                  <ChevronRight className="ml-2" size={20} />
                </motion.a>
              </motion.div>

              {/* Right: Engine Visual */}
              <motion.div
                className="relative flex items-center justify-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.4, type: 'spring' }}
              >
                <img
                  // @ts-ignore - This works with bundlers like Webpack (Create React App)
                  src={require('./assets/engine.jpeg')}
                  alt="Transformer Engine Visual"
                  className="rounded-xl shadow-2xl w-[85%] mx-auto floating"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-[var(--card-bg)]">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card p-6 rounded-2xl hover-lift text-center"
                >
                  <stat.icon className="mx-auto mb-4 text-[var(--accent-blue)]" size={40} />
                  <div className="text-3xl md:text-4xl font-black gradient-text-flow mb-2">{stat.value}</div>
                  <div className="text-sm text-[var(--text-color-secondary)]">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Vision Section */}
        <section className="py-28">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto text-center mb-20"
            >
              <h2 className="text-4xl md:text-5xl font-black mb-8">
                <span className="gradient-text-flow">AI That Belongs to You</span>
              </h2>
              <p className="text-xl text-[var(--text-color-secondary)] leading-relaxed mb-6">
                In a world of cloud-based AI, true privacy and offline capability are rare. We believe the future of AI is not just in the cloud, but <span className="font-bold text-[var(--text-color)]">on your device</span>, under your control.
              </p>
              <p className="text-lg text-[var(--text-color-secondary)] leading-relaxed">
                The Transformer Engine was born from a simple, powerful idea: what if you could run a state-of-the-art AI agent directly on your own machine, without an internet connection, with the speed of a local application?
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card p-8 rounded-2xl hover-lift"
                >
                  <div className="flex items-start space-x-4">
                    <div
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: `${feature.color}20` }}
                    >
                      <feature.icon size={32} style={{ color: feature.color }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-[var(--text-color)] mb-3">{feature.title}</h3>
                      <p className="text-[var(--text-color-secondary)]">{feature.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Open Platform Section */}
        <section className="py-28 bg-[var(--card-bg)] relative overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 left-0 w-full h-full" style={{ 
              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 50px, var(--accent-blue) 50px, var(--accent-blue) 51px)',
            }} />
          </div>

          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-black mb-6">
                  <span className="gradient-text-flow">An Open Platform</span>
                  <br />
                  <span className="text-[var(--text-color)]">for a World of Models</span>
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="glass-card p-8 rounded-2xl hover-lift"
                >
                  <GitBranch className="text-[var(--accent-cyan)] mb-4" size={48} />
                  <h3 className="text-2xl font-bold text-[var(--text-color)] mb-4">Your Models, Your Engine</h3>
                  <p className="text-[var(--text-color-secondary)] leading-relaxed">
                    Fully compatible with a wide range of open-source models, including advanced Vision-Language Models (VLMs) and Large Language Models (LLMs).
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="glass-card p-8 rounded-2xl hover-lift bg-gradient-to-br from-blue-500/10 to-teal-500/10"
                >
                  <Shield className="text-[var(--accent-blue)] mb-4" size={48} />
                  <h3 className="text-2xl font-bold text-[var(--text-color)] mb-4">The SoulDubu Advantage</h3>
                  <p className="text-[var(--text-color-secondary)] leading-relaxed">
                    Our free "SoulDubu Mini" model will be distributed exclusively through our desktop application, ensuring a secure, optimized, and seamless experience.
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Future Section */}
        <section className="py-28">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-6xl mx-auto glass-card p-12 md:p-16 rounded-3xl hover-lift pulse-glow relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-br from-teal-500/20 to-emerald-500/20 rounded-full blur-3xl" />

              <div className="relative z-10">
                <div className="flex items-center justify-center mb-8">
                  <Layers className="text-[var(--brand-color-lime)] mr-4" size={56} />
                  <h2 className="text-4xl md:text-5xl font-black gradient-text-flow">The Future is Local</h2>
                </div>

                <div className="space-y-6 text-lg text-[var(--text-color-secondary)] leading-relaxed max-w-4xl mx-auto">
                  <p>
                    The Transformer Engine is more than just an inference engine; it's a <span className="font-bold text-[var(--text-color)]">declaration of our belief</span> in a new paradigm for AI.
                  </p>
                  <p>
                    A future where the most powerful technology is not locked away in distant data centers, but runs <span className="font-bold text-[var(--accent-blue)]">securely and instantly</span> on the devices you use every day.
                  </p>
                  <p className="text-xl font-semibold text-[var(--text-color)]">
                    It is the foundation upon which we will build a new generation of AI tools—private, powerful, and truly personal.
                  </p>
                </div>

                <div className="text-center mt-12">
                  <motion.a
                    href="/#download"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="glow-button py-5 px-12 rounded-full text-xl font-bold shadow-2xl inline-flex items-center"
                  >
                    <Download className="mr-3" size={28} />
                    Download SoulDubu Desktop App
                    <ChevronRight className="ml-3" size={24} />
                  </motion.a>
                  <p className="text-sm text-[var(--text-color-secondary)] mt-4">
                    Experience the Power of the Transformer Engine Today
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[var(--header-bg)] backdrop-filter backdrop-blur-md text-[var(--text-color-secondary)] py-16 md:py-20 mt-20 border-t border-[var(--card-border)]">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-16 mb-10 md:mb-12">
              <div className="col-span-2">
                <h3 className="text-2xl font-bold text-[var(--text-color)] mb-4">QueenDahyun AGI</h3>
                <p className="text-sm">The Future of AI is Local, Private, and Powerful.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4 text-[var(--text-color)]">Links</h3>
                <ul className="space-y-2">
                  <li><a href="/" className="hover:text-[var(--text-color)] transition duration-300 text-sm">Home</a></li>
                  <li><a href="/Team" className="hover:text-[var(--text-color)] transition duration-300 text-sm">Team</a></li>
                  <li><a href="/research" className="hover:text-[var(--text-color)] transition duration-300 text-sm">Research</a></li>
                  <li><a href="/blog" className="hover:text-[var(--text-color)] transition duration-300 text-sm">Blog</a></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4 text-[var(--text-color)]">Contact</h3>
                <ul className="space-y-2">
                  <li><a href="#" className="hover:text-[var(--text-color)] transition duration-300 text-sm">Support</a></li>
                  <li><a href="#" className="hover:text-[var(--text-color)] transition duration-300 text-sm">Documentation</a></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-[var(--card-border)] mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center text-sm">
              <p className="mb-4 sm:mb-0">
                Copyright © {new Date().getFullYear()} QueenDahyun AGI. All Rights Reserved.
              </p>
              <div className="flex space-x-6">
                <a href="https://www.linkedin.com/company/queendahyun" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--text-color)] transition-colors">
                  <Linkedin size={20} />
                </a>
                <a href="https://x.com/queendahyun_agi" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--text-color)] transition-colors">
                  <Twitter size={20} />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TransformerEngine;